import { jwtDecode } from "jwt-decode";
import usersRequest from "../../request/usersRequest";
import companyBasedRequest from "../../request/companyBasedRequest";
import { clearTokens, getStoredTokens, getValidAccessToken, hasSession, RefreshUnavailableError, SessionExpiredError } from "../../auth/tokenManager";

/* The whole app sits behind a loader until this resolves, so boot gets one
   shared budget rather than a per-step timeout that can stack up. */
const BOOT_BUDGET_MS = 15000;
const MIN_STEP_MS = 2500;

/**
 * A fetch failed for a reason that says nothing about the session: the network,
 * a timeout, or the API itself being unwell. None of these are a logout.
 */
function isTransport(err) {
	const message = err?.message || "";
	return (
		err instanceof RefreshUnavailableError ||
		err?.name === "AbortError" ||
		err?.name === "TypeError" ||
		/network|failed to fetch|timed out/i.test(message) ||
		/\b5\d\d\b/.test(message)
	);
}

function cached(key) {
	try {
		const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

function userFromIdToken(idToken) {
	const decoded = jwtDecode(idToken);
	return {
		userId: decoded.sub,
		email: decoded.email,
		firstName: decoded.given_name,
		lastName: decoded.family_name,
		phoneNumber: decoded.phone_number,
		role: decoded["custom:role"],
	};
}

/**
 * Restore the session on page load.
 *
 * Rules this follows, in order of importance:
 *   - Never hang. Every step is on a timeout.
 *   - Only sign the user out when the server or Cognito actually says no.
 *     An offline laptop or a slow Lambda cold start is not a logout.
 */
export async function getInitialUserData() {
	if (!hasSession()) return null;

	const deadline = Date.now() + BOOT_BUDGET_MS;
	const remaining = () => Math.max(MIN_STEP_MS, deadline - Date.now());

	/* 1. Make sure we hold a live access token before anything else runs. */
	try {
		await getValidAccessToken();
	} catch (err) {
		if (err instanceof SessionExpiredError) {
			/* endSession already cleared storage. */
			return null;
		}
		/* Cannot reach Cognito. Leave the stored tokens alone so a later reload
		   recovers the session instead of forcing a fresh sign-in. */
		console.warn("Token refresh unavailable at boot:", err?.message);
		return null;
	}

	const { idToken, accessToken, refreshToken } = getStoredTokens();

	/* 2. Decode a provisional user so we have something to render. */
	let user;
	try {
		user = userFromIdToken(idToken);
	} catch {
		clearTokens();
		return null;
	}

	/* 3. Validate against /me, the source of truth. */
	let verified = false;
	try {
		const data = await usersRequest.me(accessToken, { timeoutMs: remaining() });
		if (!data?.user) throw new Error("Invalid /me response");
		user = { ...user, ...data.user };
		verified = true;
	} catch (err) {
		if (err instanceof SessionExpiredError) return null;

		if (!isTransport(err)) {
			/* The server answered and rejected us — account gone, role revoked. */
			console.warn("/me rejected the session — signing out", err?.message);
			clearTokens();
			return null;
		}

		/* Offline or the API is slow. Boot from the cached profile; every real
		   request still gets authorised server-side. */
		console.warn("/me unreachable — booting from cache:", err?.message);
		const cachedUser = cached("user");
		if (cachedUser) user = { ...user, ...cachedUser };
	}

	/* 4. Shops for admins and owners. Cached copy is a fine fallback. */
	let shop = null;
	if ((user.role === "admin" || user.role === "owner") && user.shopId) {
		try {
			const res = await companyBasedRequest.getShopWithstaffsByIdPrivate(user.shopId, accessToken, { timeoutMs: remaining() });
			shop = res?.shop || null;
			if (shop) localStorage.setItem("shop", JSON.stringify(shop));
		} catch (err) {
			if (err instanceof SessionExpiredError) return null;
			console.warn("Failed fetching shop:", err?.message);
			shop = cached("shop");
		}
	}

	return {
		cognitoTokens: {
			IdToken: idToken,
			AccessToken: accessToken,
			RefreshToken: refreshToken || null,
		},
		user,
		shop,
		verified,
	};
}
